import {
  ReactElement,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Field } from '@navozyme/uikit/dist/atoms/Field';
import { DatePickerTimeInput } from '@navozyme/uikit/dist/molecules/DatePicker';
import { format } from 'date-fns';
import { Form, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';

import {
  getCompanies,
  selectCompanies,
  selectCompaniesLoading,
} from '../../../slices/companySlice';
import {
  Document,
  getRequiredDocuments,
  getVesselCertificates,
  requestDocuments,
} from '../../../slices/documentsSlice';
import {
  createPortcallStatusLog,
  getBerth,
  getDangerousGoodsData,
  getPortcallStatusLog,
  getVessel,
  selectBerth,
  selectDangerousGoods,
  selectVesselData,
  ShipOwner,
  updateVessel,
  updateVesselBunkeringDetails,
  VesselBunkeringDetailsData,
  VesselData as VesselDataType,
} from '../../../slices/portCallSlice';

import Input from '../../../../forms/components/Input';
import MultiSelect from '../../../../forms/components/MultiSelect';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import { shipTypeOptions } from '../../../../global/globalTypes';
import { removeNullUndefined } from '../../../../global/utils/cleanEmptyData';
import { countryListAllIsoData } from '../../../../global/utils/countriesList';
import { API_REQUEST_FORMAT_DATE } from '../../../../global/utils/datetimes';
import { europeanCountriesList } from '../../../../global/utils/europeanCountriesList';
import Page from '../../../../layout/components/Page';
import PageHeader from '../../../../layout/components/PageHeader';
import {
  CERTIFICATES,
  DocumentCategory,
  DocumentSubCategory,
} from '../../documents/constants';
import BunkeringDetails from '../../port-call-data/components/BunkeringDetails';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';
import { addOrRemoveCertificates } from '../../ship-certificates/handleCertificates';

import '../styles/vessel-data.scss';

const validationSchema = Yup.object({
  imo: Yup.number().test('len', 'Must be exactly 7 numbers', (val) => {
    if (val) {
      return val.toString().length === 7;
    }
    return true;
  }),
  mmsi: Yup.number().test('len', 'Must be exactly 9 numbers', (val) => {
    if (val) {
      return val.toString().length === 9;
    }
    return true;
  }),
  shipType: Yup.string().oneOf(shipTypeOptions, 'Invalid ship type'),
  length: Yup.number().min(1, 'Must be greater than 0'),
  breadth: Yup.number().min(1, 'Must be greater than 0'),
  hullConfiguration: Yup.string().required('Hull configuration is required'),
});

type ShipOwnerOption = {
  value: ShipOwner;
  label: string;
};

const VesselData = () => {
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const navigate = useNavigate();
  const vesselData = useSelector(selectVesselData);
  const berth = useSelector(selectBerth);
  const dangerousGoods = useSelector(selectDangerousGoods);
  const [selectedShipOwnerOption, setSelectedShipOwnerOption] =
    useState<ShipOwnerOption | null>(null);
  const companies = useSelector(selectCompanies);
  const isSelectCompaniesLoading = useSelector(selectCompaniesLoading);
  const [dropdownOptions, setDropdownOptions] = useState<ShipOwnerOption[]>([]);
  const formId = 'VESSEL_DATA_FORM';

  useEffect(() => {
    if (params.uid) {
      dispatch(getVessel(params.uid));
      dispatch(getDangerousGoodsData(params.uid));
      dispatch(getBerth(params.uid));
    }
  }, [dispatch, params.uid]);

  const formRef = useRef(null);
  const { formContextRef } = useContext(FormRefContext);
  const bunkeringFormRef =
    useRef<FormikProps<VesselBunkeringDetailsData>>(null);

  useEffect(() => {
    formContextRef.current = formRef.current;
    if (vesselData?.shipOwner) {
      setDropdownOptions([
        { label: vesselData.shipOwner.name, value: vesselData.shipOwner },
      ]);
      setSelectedShipOwnerOption({
        label: vesselData.shipOwner.name,
        value: vesselData.shipOwner,
      });
    }
  }, [formContextRef, formRef, vesselData, dangerousGoods]);

  useEffect(() => {
    if (companies && companies.length > 0) {
      const options = companies.map((company) => {
        return {
          label: company.name,
          value: company,
        };
      });
      setDropdownOptions(options);
    }
  }, [companies]);

  const handleSearchCompanyChanged = _.debounce((value) => {
    if (value && value.length >= 3) {
      dispatch(getCompanies({ name: value, companyType: 'ship_owner' }));
    }
  }, 500);

  const handleSearchCompanyChangeDebounced = (value: string) => {
    if (handleSearchCompanyChanged.cancel) {
      handleSearchCompanyChanged.cancel();
    }
    handleSearchCompanyChanged(value);
  };

  const maxDate = new Date();

  const initialValues = {
    hullConfiguration: 'SINGLE_HULL',
    ...vesselData,
    imo: vesselData.imo ?? '',
    mmsi: vesselData.mmsi ?? '',
    shipType: vesselData.shipType,
    buildDate: vesselData.buildDate ? new Date(vesselData.buildDate) : null,
    shipOwner: vesselData.shipOwner ? vesselData.shipOwner : null,
  };

  const hullConfigurationOptions = [
    { key: 'Single hull', value: 'SINGLE_HULL' },
    { key: 'Single hull SBT', value: 'SINGLE_HULL_BST' },
    { key: 'Double hull', value: 'DOUBLE_HULL' },
  ];

  const updateBunkeringDetails = async (uid: string) => {
    if (!bunkeringFormRef.current) return false;

    try {
      await bunkeringFormRef.current.submitForm();
      const values = bunkeringFormRef.current.values;
      const response = await dispatch(
        updateVesselBunkeringDetails({
          uid,
          data: values,
        })
      );
      return !updateVesselBunkeringDetails.rejected.match(response);
    } catch (error) {
      console.error('Error updating bunkering details:', error);
      return false;
    }
  };

  const handleSubmit = async (values: VesselDataType) => {
    if (params.uid) {
      const finalValues = { ...values };
      finalValues.shipOwner =
        selectedShipOwnerOption?.value ?? vesselData?.shipOwner;

      if (values.buildDate) {
        finalValues.buildDate = format(
          new Date(values.buildDate),
          API_REQUEST_FORMAT_DATE
        );
      }
      finalValues.doubleHull = finalValues?.hullConfiguration === 'DOUBLE_HULL';

      const isSuccessUpdateVessel = await dispatch(
        updateVessel({
          uid: params.uid,
          data: removeNullUndefined(finalValues) as VesselDataType,
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) {
          return false;
        } else {
          return true;
        }
      });
      if (!isSuccessUpdateVessel) {
        return;
      }

      if (berth.berthStays[0]?.suppliesBunkering) {
        const isSuccessUpdateBunkeringDetails = await updateBunkeringDetails(
          params.uid
        );

        if (!isSuccessUpdateBunkeringDetails) {
          return;
        }
      }

      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.GeneralDeclarationVesselData,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) {
          return false;
        } else {
          return true;
        }
      });
      if (!isSuccessUpdateStatus) {
        return;
      }

      dispatch(getPortcallStatusLog(params.uid));

      const vesselDocuments = await dispatch(
        getVesselCertificates({ vesselUid: vesselData.uid })
      );
      type VesselCertificatesPayload = { documents?: Document[] };
      const payload = vesselDocuments?.payload as VesselCertificatesPayload;
      let vesselCertificates = payload?.documents;
      vesselCertificates = vesselCertificates ? [...vesselCertificates] : [];

      const requiredDocuments = await dispatch(
        getRequiredDocuments({ uid: params.uid })
      );
      type RequiredDocumentsPayload = {
        documents: { [key: string]: Document[] };
      };
      const requiredDocumentsPayload =
        requiredDocuments?.payload as RequiredDocumentsPayload;
      let requestedCertificates =
        requiredDocumentsPayload?.documents?.requestedCertificates;
      requestedCertificates = requestedCertificates
        ? [...requestedCertificates]
        : [];

      const initialRequestedCertificates = JSON.stringify(
        requestedCertificates
      );

      const isEuropeanFlag = europeanCountriesList.some(
        (country) => country.value === finalValues.flag
      );

      if (isEuropeanFlag) {
        requestedCertificates = addOrRemoveCertificates(
          vesselCertificates,
          requestedCertificates,
          CERTIFICATES.IHM.id,
          CERTIFICATES.IHM.name,
          DocumentSubCategory.HongKongConventionAndEUShipRecyclingRegulation,
          DocumentCategory.OFFICIAL_LIST_CERTIFICATES
        );
      } else {
        requestedCertificates = requestedCertificates.filter(
          (c: Document) =>
            ![CERTIFICATES.ISO_14001.id, CERTIFICATES.IHM.id].includes(
              c.documentType.id
            )
        );
      }

      const finalRequestedCertificates = JSON.stringify(requestedCertificates);
      if (
        finalRequestedCertificates !== initialRequestedCertificates &&
        params.uid
      ) {
        const isSuccessReqCert = dispatch(
          requestDocuments({
            uid: params.uid,
            category: 'requestedCertificates',
            data: requestedCertificates,
          })
        ).then((response) => {
          if (response.type && response.type.endsWith('/rejected')) {
            return false;
          } else {
            return true;
          }
        });

        if (!isSuccessReqCert) {
          return;
        }
      }

      navigate(`/${params.uid}/inspections`);
    }
  };

  return (
    <Page>
      <div className="vessel-data">
        <div className="vessel-data__header">
          <PageHeader
            title={'Vessel data'}
            subtitle={`Please, verify if the data of the vessel is correct.`}
          />
        </div>
        <Formik
          onSubmit={handleSubmit}
          innerRef={formRef}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}>
          {({ values, errors, setFieldValue }) => (
            <Form
              className="vessel-data-form"
              id={formId}
              data-testid="vessel-data-form">
              <div className="vessel-data-form__controls">
                <div className="vessel-data-form__first-row">
                  <div className="vessel-data-form__first-column">
                    <Input
                      name="imo"
                      type="number"
                      placeholder="IMO Number"
                      label="IMO Number"
                    />
                    <Input
                      name="shipName"
                      placeholder="Ship name"
                      label="Ship name"
                    />
                    <Input
                      name="shipType"
                      placeholder="Ship type"
                      label="Ship type"
                    />
                    <Input
                      name="grossTonnage"
                      type="number"
                      placeholder="Gross tonnage (m)"
                      label="Gross tonnage (m)"
                    />
                    <Input
                      name="deadweight"
                      type="number"
                      placeholder="Deadweight tonnage (m)"
                      label="Deadweight tonnage (m)"
                    />
                    <Input
                      name="length"
                      type="number"
                      placeholder="Length"
                      label="Length"
                    />
                  </div>
                  <div className="vessel-data-form__second-column">
                    <MultiSelect
                      className={'multiselect'}
                      name="flag"
                      options={countryListAllIsoData}
                      isMulti={false}
                      label="Flag state"
                      placeholder="Flag state"
                    />
                    <Input
                      name="callsign"
                      placeholder="Call sign"
                      label="Call sign"
                    />
                    <Input
                      name="mmsi"
                      type="number"
                      placeholder="MMSI number"
                      label="MMSI number"
                    />
                    <Field label="Date of build" className="field_date_picker">
                      <DatePickerTimeInput
                        key={initialValues?.buildDate + 'buildDate'}
                        name="buildDate"
                        sizeInput="small"
                        maxDate={maxDate}
                        showTime={false}
                        placeholder="Date of build"
                        defaultValue={initialValues?.buildDate}
                        action={({ isoDate }: { isoDate: string }) => {
                          const finalBuildDate =
                            !isoDate || isoDate === null || isoDate === ''
                              ? null
                              : new Date(isoDate);
                          setFieldValue('buildDate', finalBuildDate);
                        }}
                        hasError={errors?.buildDate}
                        errorMessage={errors?.buildDate}
                        popperContainer={({
                          children,
                        }: {
                          children: ReactElement;
                        }) => (
                          <div
                            style={{
                              position: 'fixed',
                              zIndex: 9999,
                            }}>
                            {children}
                          </div>
                        )}
                        popperProps={{
                          strategy: 'fixed',
                        }}
                      />
                    </Field>

                    <MultiSelect
                      className={'multiselect'}
                      name="shipOwner"
                      options={dropdownOptions}
                      isMulti={false}
                      label="Ship company name"
                      isLoading={isSelectCompaniesLoading}
                      placeholder={
                        dropdownOptions?.length
                          ? dropdownOptions[0].label
                          : 'Ship company name'
                      }
                      onInputChange={handleSearchCompanyChangeDebounced}
                      onChange={(selected: ShipOwnerOption) => {
                        values.shipOwner = selected?.value;
                        setSelectedShipOwnerOption(selected);
                      }}
                    />
                    <Input
                      name="breadth"
                      type="number"
                      placeholder="Breadth (m)"
                      label="Breadth (m)"
                    />
                  </div>
                </div>
                <div className="vessel-data-form__second-row">
                  <RadioButtonsCustom
                    name="hullConfiguration"
                    label="Hull configuration *"
                    options={hullConfigurationOptions}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {berth.berthStays[0]?.suppliesBunkering ? (
          <>
            <hr className="port-call-data__divider" />
            <BunkeringDetails
              ref={
                bunkeringFormRef as RefObject<
                  FormikProps<VesselBunkeringDetailsData>
                >
              }
            />
          </>
        ) : null}
        <SkipAndNextButtons
          next={`/${params.uid}/inspections`}
          back={`/${params.uid}/port-call-data`}
          submitDisabled={false}
          formId={formId}
        />
      </div>
    </Page>
  );
};

export default VesselData;
